import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { tenantAPI } from '../../services/api';
import axios from 'axios';
import PageTitle from '../shared/PageTitle';

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [showNewTenantModal, setShowNewTenantModal] = useState(false);
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    admin_email: '',
    admin_password: '',
    subscription_plan: '', // Will be set to first plan ID
    subscription_status: 'active'
  });
  const [formErrors, setFormErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchTenants();
    fetchSubscriptionPlans();
  }, []);

  const fetchSubscriptionPlans = async () => {
    try {
      // Use relative path in production, localhost in development
      const apiBaseURL = process.env.REACT_APP_API_URL ||
        (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');

      const response = await axios.get('/api/saas/subscriptions/plans', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(apiBaseURL && { baseURL: apiBaseURL })
      });

      setPlans(response.data);

      // Set default subscription plan to the first available plan
      if (response.data && response.data.length > 0) {
        setFormData(prev => ({
          ...prev,
          subscription_plan: response.data[0].id.toString()
        }));
      }
    } catch (error) {
      toast.error('Failed to load subscription plans');
    }
  };

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await tenantAPI.getAllTenants();
      // Ensure each tenant has a status property and consistent ID
      const processedTenants = response.data.map(tenant => {
        // Prioritize subscription_status over old status field
        const tenantStatus = tenant.subscription_status || tenant.status || 'active';
        // Ensure we have a consistent ID field
        const tenantId = tenant._id || tenant.id;
        return {
          ...tenant,
          id: tenantId,
          _id: tenantId, // Include both ID formats
          status: tenantStatus // Override status with subscription_status
        };
      });

      setTenants(processedTenants);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load organizations');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Organization name is required';
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!showEditTenantModal && !formData.admin_password.trim()) errors.admin_password = 'Admin password is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    // Set loading indicator immediately
    setCreateLoading(true);

    // Skip the timeout approach - instead, rely on proper API response handling
    // We'll still keep a minimal timeout just to ensure the UI doesn't get stuck indefinitely
    const safetyTimeout = setTimeout(() => {
      setCreateLoading(false);
      setShowNewTenantModal(false);
      fetchTenants(); // Refresh tenant list silently
      // No toast message from timeout - we'll let the proper completion handling show messages
    }, 5000); // Short 5-second timeout just to ensure UI responsiveness

    try {
      const user = JSON.parse(localStorage.getItem('user'));

      // Get the selected plan to check if it's free
      const selectedPlan = plans.find(plan => plan.id.toString() === formData.subscription_plan);
      const isFreePlan = selectedPlan && selectedPlan.price === 0;

      // Add subscription period to the form data
      const formDataWithSubscription = {
        ...formData,
        subscription_period: 'monthly',  // Default to monthly
        subscription_status: isFreePlan ? 'active' : 'active',
        user_role: user?.role,  // Set active for free plans, pending for paid plans
      };

      // Use a try/catch inside to better handle server errors
      let response;
      try {
        response = await tenantAPI.createTenant(formDataWithSubscription, true); // Allow mock data for timeouts 
      }
      catch (serverError) {
        // Show a more specific error message based on the server response
        if (serverError.response?.status === 500) {
          toast.error('Server error: Organization could not be created. Please try again later or contact support.');
        } else if (serverError.response?.data?.message) {
          toast.error(`Error: ${serverError.response.data.message}`);
        } else {
          toast.error('Failed to create organization. Server returned an error.');
        }

        throw serverError; // Re-throw to be caught by outer catch
      }

      // Clear the safety timeout since we got a response
      clearTimeout(safetyTimeout);

      // Check if this was a timeout response
      const isTimeoutResponse = response?.timeout_occurred === true;


      // Check if we got a real response from the server or a mock/fallback response
      const isMockResponse = response?.isMockData === true && !isTimeoutResponse;

      // Check subscription status and plan
      const subscriptionStatus = response?.data?.subscription_status ||
        response?.data?.subscription?.status ||
        (isFreePlan ? 'active' : 'pending');

      const subscriptionPlan = response?.data?.subscription_plan_name ||
        getPlanName(response?.data?.subscription_plan ||
          response?.data?.subscription?.plan ||
          formData.subscription_plan);

      // Close the modal first
      setShowNewTenantModal(false);

      // Use a single unified success message that combines all the information
      if (isMockResponse) {
        toast.warning('Unable to connect to server. Organization may not have been created.');
      } else {
        if (subscriptionStatus === 'active') {
          toast.success(`Organization "${formData.name}" created successfully with ${subscriptionPlan} plan`);
        } else {
          // Single message that covers both the success and the pending subscription status
          toast.success(`Organization "${formData.name}" created successfully with ${subscriptionPlan} plan. Subscription status is pending - please approve it.`);
        }
      }

      resetForm();
      setCreateLoading(false);

      fetchTenants();
    } catch (error) {
      // Clear the safety timeout
      clearTimeout(safetyTimeout);

      // Check for specific error messages in the API response
      if (error.response && error.response.data) {
        // Show the specific error from the API
        toast.error(error.response.data.message || 'Failed to create organization');

        // If there's a validation error about username
        if (error.response.data.message && error.response.data.message.includes('Username already in use')) {
          setFormErrors(prev => ({ ...prev, username: 'Username already in use by another organization' }));
        }
        // If there's a validation error about email
        if (error.response.data.message && error.response.data.message.includes('Email already in use')) {
          setFormErrors(prev => ({ ...prev, admin_email: 'Email already in use by another user' }));
        }
      } else {
        toast.error('Failed to create organization. Please try again.');
      }

      // Explicitly log and set loading to false 
      setCreateLoading(false);
    } finally {
      // Clear the safety timeout in case it wasn't cleared in the try/catch blocks
      clearTimeout(safetyTimeout);

      // Always ensure loading is set to false regardless of success or failure 
      setCreateLoading(false);
    }
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      // Extract the fields we want to send to the API
      const updateData = {
        name: formData.name,
        username: formData.username,
        admin_email: formData.admin_email,
        subscription_plan: formData.subscription_plan,
        subscription_status: formData.subscription_status,
        // If the plan changed, update the subscription end date
        subscription_updated: formData.subscription_plan !== (currentTenant.subscription_plan || currentTenant.subscription?.plan)
      };

      // Only include password if it was provided
      if (formData.admin_password.trim()) {
        updateData.admin_password = formData.admin_password;
      }

      // Get the correct ID value - use either _id or id, whichever is available
      const tenantId = currentTenant._id || currentTenant.id;
      const response = await tenantAPI.updateTenant(tenantId, updateData);

      // Check for subscription changes
      const subscriptionChanged =
        formData.subscription_plan !== (currentTenant.subscription_plan || currentTenant.subscription?.plan);

      // Show success toast with status change information if status was changed
      if (response.data.statusChanged) {
        toast.success(`Organization status changed from ${response.data.previousStatus || 'active'} to ${response.data.status}`);
      } else {
        toast.success('Organization updated successfully');
      }

      // Show subscription change notification if applicable
      if (subscriptionChanged) {
        const newPlan = plans.find(p => p.id.toString() === formData.subscription_plan);
        const newPlanName = newPlan ? newPlan.name : `Plan ID ${formData.subscription_plan}`;
        toast.info(`Subscription plan changed to ${newPlanName}`);
      }

      setShowEditTenantModal(false);
      resetForm();
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update organization');
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      return;
    }

    try {
      await tenantAPI.deleteTenant(tenantId);

      toast.success('Organization deleted successfully');
      fetchTenants();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error(error.response?.data?.message || 'Failed to delete organization');
    }
  };

  const openEditModal = (tenant) => {
    // Make a deep copy to preserve original tenant data for comparison
    setCurrentTenant({ ...tenant });

    // Determine the subscription plan ID from various possible sources
    let subscriptionPlanId = '';

    // If tenant has a subscription_plan that looks like an ID (number), use it
    if (tenant.subscription_plan && !isNaN(tenant.subscription_plan)) {
      subscriptionPlanId = tenant.subscription_plan.toString();
    }
    // If it's a plan name, find the corresponding plan ID
    else if (tenant.subscription_plan) {
      const matchingPlan = plans.find(plan => plan.name === tenant.subscription_plan);
      subscriptionPlanId = matchingPlan ? matchingPlan.id.toString() : '';
    }
    // Default to first plan if no subscription plan
    else if (plans.length > 0) {
      subscriptionPlanId = plans[0].id.toString();
    }

    setFormData({
      name: tenant.name,
      username: tenant.username,
      admin_email: tenant.admin_email || tenant.contact_email,
      subscription_plan: subscriptionPlanId,
      admin_password: '', // Don't show the password
      subscription_status: tenant.subscription_status || tenant.subscription?.status || 'active'
    });

    setShowEditTenantModal(true);
    setFormErrors({});
  };

  // Helper function to get plan name from plan ID
  const getPlanName = (planIdOrName) => {
    // If it's already a known plan name (like 'Free', 'Pro'), return it
    if (planIdOrName && typeof planIdOrName === 'string' && isNaN(planIdOrName)) {
      return planIdOrName;
    }

    // If it's a plan ID (number), find the corresponding plan name
    if (planIdOrName && !isNaN(planIdOrName)) {
      const plan = plans.find(p => p.id == planIdOrName);
      return plan ? plan.name : planIdOrName;
    }

    return planIdOrName || 'No Plan';
  };

  const resetForm = () => {
    setFormData({
      name: '',
      username: '',
      admin_email: '',
      admin_password: '',
      subscription_plan: '', // Will be set to first plan ID
      subscription_status: 'active'
    });
    setFormErrors({});
    setCurrentTenant(null);
  };

  const filteredTenants = tenants.filter(tenant => {
    // Apply search filter
    const matchesSearch =
      tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tenant.admin_email.toLowerCase().includes(searchQuery.toLowerCase());

    // Apply status filter - prioritize subscription_status
    const currentStatus = tenant.subscription_status || tenant.status || 'active';
    const matchesStatus = filterStatus === 'all' || filterStatus === currentStatus;

    return matchesSearch && matchesStatus;
  });

  // Add diagnostic function to component
  const runDiagnosticTest = async () => {
    try {
      toast.info('Running tenant creation diagnostic test...');
      const result = await tenantAPI.diagnoseTenantCreationIssue();

      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Error running diagnostic test');
    }
  };

  // Add a function to approve pending subscriptions
  const approveSubscription = async (tenant) => {
    try {
      setLoading(true);

      // Prepare update data - only updating subscription status
      const updateData = {
        subscription_status: 'active',
        subscription_updated: true
      };
      await tenantAPI.updateTenant(tenant.id, updateData);
      toast.success(`Subscription approved for ${tenant.name}`);
      fetchTenants();
    } catch (error) {
      toast.error('Failed to approve subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-4">
      <PageTitle
        icon="fa-building"
        title="Organizations"
        action={
          <div>
            {process.env.NODE_ENV === 'development' && (
              <button
                className="btn btn-outline-secondary mb-2 me-2"
                onClick={runDiagnosticTest}
                title="Run diagnostic test for tenant creation"
              >
                <i className="fas fa-bug me-1"></i>
                Diagnose
              </button>
            )}
            <button
              className="btn btn-primary mb-2"
              onClick={() => {
                resetForm();
                setShowNewTenantModal(true);
              }}
            >
              <i className="fas fa-plus-circle me-2"></i>
              Add New Organization
            </button>
          </div>
        }
      />

      {/* Filters */}
      <div className="card shadow mb-4">
        <div className="py-3 superadmin-section">
          <h6 className="m-0 font-weight-bold text-primary">Filters</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, username, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="btn btn-outline-secondary searchFilterButton" type="button">
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <select
                className="form-control"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tenants Table */}
      <div className="card shadow mb-4">
        <div className="py-3 superadmin-section">
          <h6 className="m-0 font-weight-bold text-primary">Organizations</h6>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Admin Email</th>
                    <th>Plan</th>
                    <th>Subscription Status</th>
                    <th>Organization Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenants.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center">No organizations found</td>
                    </tr>
                  ) : (
                    filteredTenants.map(tenant => (
                      <tr key={tenant.id}>
                        <td>{tenant.name}</td>
                        <td>{tenant.username}</td>
                        <td>{tenant.admin_email}</td>
                        <td>
                          {getPlanName(tenant.subscription_plan || tenant.subscription?.plan)}
                        </td>
                        <td className='d-flex align-items-center' style={{ paddingBlockStart: '12px' }}>
                          <span className={`badge bg-${tenant.subscription_status === 'active' ||
                            tenant.subscription?.status === 'active' ? 'success' :
                            tenant.subscription_status === 'pending' ||
                              tenant.subscription?.status === 'pending' ? 'warning' : 'danger'
                            }`}>
                            {tenant.subscription_status ||
                              tenant.subscription?.status ||
                              'inactive'}
                          </span>
                          {(tenant.subscription_status === 'pending' || tenant.subscription?.status === 'pending') && (
                            <button
                              className="btn btn-sm btn-outline-success ms-2"
                              onClick={() => approveSubscription(tenant)}
                              title="Approve this subscription"
                            >
                              Approve
                            </button>
                          )}
                        </td>
                        <td>
                          {/* Show subscription_status with fallback to old status */}
                          {(() => {
                            const orgStatus = tenant.subscription_status || tenant.status || 'active';
                            let badgeType = 'danger';
                            if (orgStatus === 'active') badgeType = 'success';
                            else if (orgStatus === 'pending' || orgStatus === 'suspended') badgeType = 'warning';
                            return (
                              <span className={`badge bg-${badgeType}`}>
                                {orgStatus}
                              </span>
                            );
                          })()}
                        </td>
                        <td>{new Date(tenant.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-info mr-2"
                              onClick={() => openEditModal(tenant)}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteTenant(tenant.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* New Tenant Modal */}
      {showNewTenantModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-dark">Add New Organization</h5>
                <button type="button" className="btn-close" onClick={() => setShowNewTenantModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleCreateTenant}>
                  <div className="mb-3">
                    <label className="form-label">Organization Name</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                    {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.username ? 'is-invalid' : ''}`}
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                    />
                    {formErrors.username && <div className="invalid-feedback">{formErrors.username}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Admin Email</label>
                    <input
                      type="email"
                      className={`form-control ${formErrors.admin_email ? 'is-invalid' : ''}`}
                      name="admin_email"
                      value={formData.admin_email}
                      onChange={handleInputChange}
                    />
                    {formErrors.admin_email && <div className="invalid-feedback">{formErrors.admin_email}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Admin Password</label>
                    <input
                      type="password"
                      className={`form-control ${formErrors.admin_password ? 'is-invalid' : ''}`}
                      name="admin_password"
                      value={formData.admin_password}
                      onChange={handleInputChange}
                    />
                    {formErrors.admin_password && <div className="invalid-feedback">{formErrors.admin_password}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Subscription Plan</label>
                    <select
                      className="form-control"
                      name="subscription_plan"
                      value={formData.subscription_plan}
                      onChange={handleInputChange}
                    >
                      {plans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - ₹{parseFloat(plan.price).toFixed(2)}/{plan.billing_cycle}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      A subscription will be created with this plan. The organization will be billed according to the plan&apos;s pricing.
                    </small>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowNewTenantModal(false)}>Cancel</button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={createLoading}
                    >
                      {createLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating...
                        </>
                      ) : 'Create Organization'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tenant Modal */}
      {showEditTenantModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-dark">Edit Organization</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditTenantModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleUpdateTenant}>
                  <div className="mb-3">
                    <label className="form-label">Organization Name</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                    {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.username ? 'is-invalid' : ''}`}
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                    />
                    {formErrors.username && <div className="invalid-feedback">{formErrors.username}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Admin Email</label>
                    <input
                      type="email"
                      className={`form-control ${formErrors.admin_email ? 'is-invalid' : ''}`}
                      name="admin_email"
                      value={formData.admin_email}
                      onChange={handleInputChange}
                    />
                    {formErrors.admin_email && <div className="invalid-feedback">{formErrors.admin_email}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">New Admin Password (leave blank to keep current)</label>
                    <input
                      type="password"
                      className="form-control"
                      name="admin_password"
                      value={formData.admin_password}
                      onChange={handleInputChange}
                    />
                    <small className="text-muted">Only fill this in if you want to reset the admin&apos;s password</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Subscription Plan</label>
                    <select
                      className="form-control"
                      name="subscription_plan"
                      value={formData.subscription_plan}
                      onChange={handleInputChange}
                    >
                      {plans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - ₹{parseFloat(plan.price).toFixed(2)}/{plan.billing_cycle}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Organization Status</label>
                    <select
                      className="form-control"
                      name="subscription_status"
                      value={formData.subscription_status}
                      onChange={handleInputChange}
                    >
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                    <small className="text-muted">Control access to the organization&apos;s portal</small>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowEditTenantModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Update Organization</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tenants; 